import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CardContent } from "@/components/ui/card";
import partnersLogo from "@/assets/partners_logo.png";

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginFormProps {
  onSwitchToSignUp: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToSignUp }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success("Login realizado com sucesso!");
    } catch (error: unknown) {
      console.error("Login error:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao fazer login. Tente novamente.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-glass w-full rounded-2xl border border-white/10 bg-black/40 shadow-2xl backdrop-blur-xl">
      <div className="text-center pt-8 pb-4 px-4">
        <img src={partnersLogo} alt="Silva Partners" className="mx-auto h-16 sm:h-20 mb-3" />
        <h1 className="text-2xl sm:text-3xl text-foreground" style={{ fontFamily: "AwesomeSerif, serif" }}>Silva Partners</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">Entre na sua conta</p>
      </div>
      <CardContent className="px-6 sm:px-8 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                {...register("email", {
                  required: "Email é obrigatório",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email inválido",
                  },
                })}
                type="email"
                id="email"
                className="pl-10 h-11 sm:h-12"
                placeholder="seu@email.com"
              />
            </div>
            {errors.email && <p className="text-xs sm:text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">
              Senha
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                {...register("password", {
                  required: "Senha é obrigatória",
                  minLength: {
                    value: 6,
                    message: "Senha deve ter pelo menos 6 caracteres",
                  },
                })}
                type={showPassword ? "text" : "password"}
                id="password"
                className="pl-10 pr-12 h-11 sm:h-12"
                placeholder="••••••••"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-9 w-9 p-0 hover:bg-muted"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.password && <p className="text-xs sm:text-sm text-destructive">{errors.password.message}</p>}
          </div>

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="link"
              onClick={() => window.location.href = "/forgot-password"}
              className="p-0 h-auto text-xs sm:text-sm font-medium text-muted-foreground hover:text-primary"
            >
              Esqueceu sua senha?
            </Button>
          </div>

          <Button type="submit" disabled={isLoading} className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium">
            {isLoading ? "Entrando..." : "Entrar"}
          </Button>
        </form>

        <div className="mt-4 sm:mt-6 text-center">
          <p className="text-xs sm:text-sm text-muted-foreground">
            Não tem uma conta?{" "}
            <Button
              variant="link"
              onClick={onSwitchToSignUp}
              className="p-0 h-auto font-medium text-primary hover:text-primary/80"
            >
              Cadastre-se
            </Button>
          </p>
        </div>
      </CardContent>
    </div>
  );
};
